import { TARGET_FRAMERATE, decodeLagTierChangeFor, isDecodeLagEpisode } from '@playcastdotio/PeerCore';
import type {
  ClientStreamStatsMessage,
  DecodeLagEpisode,
  DecodeLagTierChange,
  QualityTier,
  StickyDowngradePolicy,
  StreamHealthEvent,
} from '@playcastdotio/PeerCore';
import { DecoderBackupMonitor } from './DecoderBackupMonitor';
import type { QualityTierDemotionHandler } from './RecoveryCapacityGate';
import type { DeepeningBlock } from './RecoveryEntryManager';

export type DecodeLagDowngradeDeps = {
  getCurrentTier?: (peerId: string) => QualityTier | undefined;
  getCurrentFps?: (peerId: string) => number | undefined;
  applyDecodeLagTierChange?: (peerId: string, change: DecodeLagTierChange) => void;
  applyCapacityTierDemotion?: QualityTierDemotionHandler;
  resolutionFallbackUnavailableReason?: string;
  stickyDowngrade?: StickyDowngradePolicy;
  /** Raise the for-cause floor (the entry manager owns the ratchet + its event). */
  raiseStickyFloor: (peerId: string, floor: 'hard' | 'tier360', reason: string) => void;
  deepeningBlock: (peerId: string) => DeepeningBlock | null;
  emitHealth: (peerId: string, event: StreamHealthEvent) => void;
  debug: (tag: string, line: Record<string, unknown>) => void;
  nowMs: () => number;
};

/**
 * DecodeLagDowngradeManager — the response to a guest whose decoder is behind.
 *
 * Observes the decoder first (DecoderBackupMonitor: debug line + health bus, never gated),
 * then decides the downgrade. STANDING DEPTH IS NOT A DECODE LAG: only a measured throughput
 * deficit changes tier or fps (`decodeLagTierChangeFor` returns null for a depth-only
 * episode), and the backup monitor's own `standing-depth` classification is checked as well
 * so the 360p switch — which reconfigures an Android decoder and roughly doubled the standing
 * buffer in the 2026-09-19 field sessions — is never the answer to a queue. A real deficit
 * still waits for the settle and standing-buffer gates like every other cut.
 */
export class DecodeLagDowngradeManager {
  private readonly decoderBackup: DecoderBackupMonitor;
  /** The decode-lag episode each guest is currently reporting; present only while active. */
  private readonly episodeByPeer = new Map<string, DecodeLagEpisode>();
  private readonly capacityDemotedPeers = new Set<string>();
  private readonly pendingCapacity = new Map<string, object>();

  constructor(private readonly deps: DecodeLagDowngradeDeps) {
    this.decoderBackup = new DecoderBackupMonitor({ emitHealth: deps.emitHealth, nowMs: deps.nowMs });
  }

  /** One guest tick: name a backed-up decoder, then apply (or refuse) the downgrade. */
  public observe(
    peerId: string,
    msg: ClientStreamStatsMessage,
    deliveredFps: number,
    standingBuffer = false,
  ): void {
    this.decoderBackup.observe(peerId, msg, deliveredFps);
    // The guest attaches the episode only while it is active; a message without one means
    // the episode has cleared.
    const episode = isDecodeLagEpisode(msg.decodeLag) && msg.decodeLag.active ? msg.decodeLag : null;
    if (!episode) {
      this.episodeByPeer.delete(peerId);
      return;
    }
    this.episodeByPeer.set(peerId, episode);
    const change = decodeLagTierChangeFor(
      this.deps.getCurrentTier?.(peerId) ?? 'top',
      this.deps.getCurrentFps?.(peerId) ?? TARGET_FRAMERATE,
      episode,
      deliveredFps,
    );
    const classification = this.decoderBackup.classificationFor(peerId);
    const depthOnly = !change || classification === 'standing-depth';
    const block = depthOnly || standingBuffer ? null : this.deps.deepeningBlock(peerId);
    this.deps.debug('[Recovery.decodeLag]', {
      peerId,
      classification: classification ?? 'unknown',
      outcome: depthOnly
        ? 'depth-only: no tier change'
        : standingBuffer
          ? 'standing-buffer: capacity path owns tier'
          : (block ?? 'applied'),
      ...(change ? { targetFramerate: change.targetFramerate } : {}),
    });
    if (!change || depthOnly || standingBuffer || block) return;
    // A disabled resolution effect may hold bitrate, but must not claim a 360p floor.
    this.deps.raiseStickyFloor(peerId, this.deps.resolutionFallbackUnavailableReason ? 'hard' : 'tier360', 'decode-lag');
    // this.deps.stickyDowngrade?.lowerFpsCeiling(peerId, change.targetFramerate);
    this.deps.applyDecodeLagTierChange?.(peerId, change);
  }

  /** Apply the resolution-only fallback after the owner's post-bitrate capacity dwell. */
  public observeSustainedCapacity(peerId: string, allowed: boolean): void {
    if (!allowed) return;
    const block = this.deps.deepeningBlock(peerId);
    if (block) {
      this.deps.debug('[Recovery.decodeLag]', { peerId, classification: 'sustained-capacity', outcome: block });
      return;
    }
    if (this.capacityDemotedPeers.has(peerId) || this.pendingCapacity.has(peerId)) return;
    if ((this.deps.getCurrentTier?.(peerId) ?? 'top') === 'bottom') return;
    const effect = this.deps.applyCapacityTierDemotion;
    if (!effect) {
      this.deps.debug('[Recovery.decodeLag]', { peerId,
        outcome: this.deps.resolutionFallbackUnavailableReason ?? 'missing-resolution-effect' });
      return;
    }
    const request = {};
    this.pendingCapacity.set(peerId, request);
    this.deps.raiseStickyFloor(peerId, 'tier360', 'sustained-capacity');
    let result: Promise<void>;
    try { result = Promise.resolve(effect(peerId, 'bottom', 'sustained-capacity')); }
    catch (error) { result = Promise.reject(error); }
    void result.then(() => {
      if (this.pendingCapacity.get(peerId) === request) this.capacityDemotedPeers.add(peerId);
    }).catch(() => {
      this.deps.debug('[Recovery.decodeLag]', { peerId, outcome: 'resolution-effect-failed' });
    }).finally(() => {
      if (this.pendingCapacity.get(peerId) === request) this.pendingCapacity.delete(peerId);
    });
  }

  /** The guest's active decode-lag episode, or null — the climb IDR is withheld while it lasts. */
  public episodeFor(peerId: string): DecodeLagEpisode | null {
    return this.episodeByPeer.get(peerId) ?? null;
  }

  public clearPeer(peerId: string): void {
    this.episodeByPeer.delete(peerId);
    this.capacityDemotedPeers.delete(peerId);
    this.pendingCapacity.delete(peerId);
    this.decoderBackup.clearPeer(peerId);
  }

  public clearAll(): void {
    this.episodeByPeer.clear();
    this.capacityDemotedPeers.clear();
    this.pendingCapacity.clear();
    this.decoderBackup.clearAll();
  }
}
